import { AppStateEnum } from '../../../../../main/types/app-state.types.ts';
import { getSpeechRecognitionUtil } from '../../../../../main/utils/audio/speech-recognition.util.ts';

import { CharacterBubbleAnimation } from '../../../../constants/character-bubble-animations.ts';
import { SPEECH_RECOGNITION_MESSAGES } from '../../../../constants/speech-recognition.ts';
import { selectCurrentLessonTaskStepKey } from '../../../../selectors/current-lesson-task-step-key.selector.ts';
import { triggerCharacterBubbleAnimationThunk } from '../../../../slices/app-state/thunks/trigger-character-bubble-animation.thunk.ts';
import { updateAppStateThunk } from '../../../../slices/app-state/thunks/update-app-state.thunk.ts';
import { setBallIsVisible } from '../../../../slices/ball/ball.slice.ts';

import { BaseAppStateChangeHandlerUtil } from '../base-app-state-change.handler.ts';

export class StoryAppAskStateChangeHandlerUtil extends BaseAppStateChangeHandlerUtil {
  private static _instance: StoryAppAskStateChangeHandlerUtil;

  public static get instance(): StoryAppAskStateChangeHandlerUtil {
    if (!StoryAppAskStateChangeHandlerUtil._instance) {
      StoryAppAskStateChangeHandlerUtil._instance = new StoryAppAskStateChangeHandlerUtil();
    }
    return StoryAppAskStateChangeHandlerUtil._instance;
  }

  private constructor(private readonly speechRecognitionUtil = getSpeechRecognitionUtil()) {
    super();
  }

  private lastAnswer: string | null = null;

  protected getHandlerName(): string {
    return 'StoryAppAsk';
  }

  protected override async onReadyToStart(): Promise<void> {
    await super.onReadyToStart();
    console.log(`${this.getHandlerName()} onReadyToStart`);
    await this.handleStartStep();
  }

  protected override async onRetry(): Promise<void> {
    await super.onRetry();
    console.log(`${this.getHandlerName()} onRetry`);
    await this.handleStartStep();
  }

  protected override async onRecording(): Promise<void> {
    console.log(`${this.getHandlerName()} onRecording`);
    const dispatch = await this.reduxStoreUtil.getDispatch();
    const state = await this.reduxStoreUtil.getRootState();
    dispatch(setBallIsVisible(true));

    try {
      // Start Speech Recognition (max 5s)
      const result = await this.speechRecognitionUtil.recognizeSpeech({
        silenceDetection: {
          enabled: true,
        },
      });

      // When it ends, use OpenResponseGrader to grade
      const task = state.lesson.currentTask!;
      const step = state.lesson.currentStep!;

      if (result.success) {
        this.lastAnswer = result.transcript;
        await this.handleAppAskStepEvaluation(task, step, result.transcript);
      } else {
        await this.handleRetryAfterError();
      }
    } catch (error) {
      await this.handleError(error as Error);
      await this.handleRetryAfterError();
    }
  }

  protected override async onCorrect(): Promise<void> {
    console.log(`${this.getHandlerName()} onCorrect`);
    await this.handleCorrect({
      answer: this.lastAnswer ?? SPEECH_RECOGNITION_MESSAGES.NO_RESPONSE,
      assuming: false,
    });
  }

  protected override async onAssumeCorrect(): Promise<void> {
    console.log(`${this.getHandlerName()} onAssumeCorrect`);
    await this.handleCorrect({
      answer: this.lastAnswer ?? SPEECH_RECOGNITION_MESSAGES.NO_RESPONSE,
      assuming: true,
    });
  }

  protected override async onIncorrect(): Promise<void> {
    console.log(`${this.getHandlerName()} onIncorrect`);
    await this.handleIncorrect(this.lastAnswer ?? SPEECH_RECOGNITION_MESSAGES.NO_RESPONSE);
  }

  protected override async onReadyForNextStep(): Promise<void> {
    console.log(`${this.getHandlerName()} onReadyForNextStep`);
    const dispatch = await this.reduxStoreUtil.getDispatch();

    const state = await this.reduxStoreUtil.getRootState();
    const stepKey = selectCurrentLessonTaskStepKey(state);
    if (this.lessonsUtil.isFinalStep(stepKey)) {
      await dispatch(triggerCharacterBubbleAnimationThunk(CharacterBubbleAnimation.SHAKE));

      await this.audioPlayerUtil.speakStep();
      await this.audioPlayerUtil.waitSpeakCompletion();

      dispatch(setBallIsVisible(true));
    } else {
      await dispatch(updateAppStateThunk({ state: AppStateEnum.MoveToNextStep }));
    }
  }

  protected override async onPlaying(): Promise<void> {
    await this.handleInvalidState();
  }

  private async handleStartStep(): Promise<void> {
    const dispatch = await this.reduxStoreUtil.getDispatch();

    await this.audioPlayerUtil.speakStep();
    await this.audioPlayerUtil.waitSpeakCompletion();

    await dispatch(updateAppStateThunk({ state: AppStateEnum.Recording })).unwrap();
  }
}

export const getStoryAppAskStateChangeHandlerUtil = (): StoryAppAskStateChangeHandlerUtil =>
  StoryAppAskStateChangeHandlerUtil.instance;
